/**
 * ===========================================
 * StudySpace ERP Frontend - Payments API Service
 * ===========================================
 * 
 * Provides API functions for cash payment recording.
 * Handles payment creation and receipt lookup.
 * 
 * IMPORTANT (NON-NEGOTIABLE):
 * This system only supports CASH payments.
 * 
 * @file src/api/payments.api.js
 * @description Payments API service
 */

import api, { handleApiError } from './axios';

/**
 * Create a payment for a session (cash only)
 * 
 * @param {Object} paymentData - Payment data
 * @param {string} paymentData.sessionId - Session ID
 * @param {string} paymentData.workspace - Workspace ID
 * @param {number} [paymentData.amount] - Optional override amount
 * @param {string} [paymentData.notes] - Optional notes
 * @returns {Promise<Object>} API response data
 */
export const createPayment = async (paymentData) => {
  try {
    const response = await api.post('/payments/session', paymentData);
    return response.data;
  } catch (error) {
    const message = handleApiError(error);
    throw new Error(message);
  }
};

/**
 * List payments with optional filters
 * 
 * @param {Object} [params={}] - Query parameters
 * @returns {Promise<Object>} API response data
 */
export const listPayments = async (params = {}) => {
  try {
    const response = await api.get('/payments', { params });
    return response.data;
  } catch (error) {
    const message = handleApiError(error);
    throw new Error(message);
  }
};

/**
 * Get payment by receipt number
 * 
 * @param {string} receiptNumber - Receipt number
 * @returns {Promise<Object>} API response data
 */
export const getPaymentByReceipt = async (receiptNumber) => {
  try {
    const response = await api.get(`/payments/receipt/${receiptNumber}`);
    return response.data;
  } catch (error) {
    const message = handleApiError(error);
    throw new Error(message);
  }
};

export default {
  createPayment,
  listPayments,
  getPaymentByReceipt
};
